import type { App } from 'vue'
import type { NavigationGuard, RouteLocation, RouteLocationNormalized, Router, RouteRecordRaw } from 'vue-router'
import type { Locale } from '@/settings/langs'
import * as Sentry from '@sentry/vue'
import { useHead } from '@vueuse/head'
import { createGtag, pageview } from 'vue-gtag'
import { createRouter, createWebHistory } from 'vue-router'
import { optGtag } from '@/components/goat/layout/cookies/Manager'
import { loadingPage } from '@/components/goat/layout/page/Loader'
import { setI18nLocale } from '@/compositions/i18nPlugin'
import { pendingGoatLink, SENSITIVE_QUERY_KEYS, takeGoatLinkParams } from '@/compositions/securedQueryParams'
import { bindParams, buildHreflangLinks, getLocalePath, getRouteLocale, isSupportedLocale } from '@/router/langRouteBuilder'
import { setVueRouter } from '@/router/routerInstance'
import { autoDetectLanguage, defaultLocale, localeToOgLocale, supportedLocales } from '@/settings/langs'
import pagesRoutes from './routes'

const MainLayout = () => import('@/layouts/MainLayout.vue')

/**
 * @field title - title and og:title
 * @field description - description and og:description
 * @field image - og:image
 * @field path - og:url and canonical
 * @field templateVariables - default values of variables used by template strings
 * @field bodyClass - body class for page
 * @field locale - locale of page; if not set, default locale is used
 * @field routerGroup - router group name; used for hreflang links generation
 */
export interface PageBasicData {
  title?: string
  description?: string
  image?: string
  path?: string
  templateVariables?: Record<string, unknown>
  bodyClass?: string
  locale?: string
  routerGroup?: string
}

interface PageMetaData {
  meta: {
    pageData?: PageBasicData
  }
  ssg?: Record<string, string>[]
}
export type RoutesData = RouteRecordRaw & PageMetaData
export type RouteWithPageMetaData = RouteLocationNormalized

const RE_TEMPLATE_VARIABLE = /\{\s*(\w+)\s*\}/g // eg {property_name}

export function replaceTemplateVariables(template: string, data: Record<string, unknown> | undefined) {
  if (data) {
    return template.replace(RE_TEMPLATE_VARIABLE, (_, token) => String(data[token] || `{${token}}`))
  }
  return template
}

export function isString(value: unknown): value is string {
  return typeof value === 'string'
}

function toStringParams(params: Record<string, string | string[]>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).map(([k, v]) => [k, Array.isArray(v) ? (v[0] ?? '') : v])
  )
}

const reactiveHeadData = {
  title: ref(''),
  description: ref(''),
  image: ref(''),
  path: ref(''),
  bodyClass: ref(''),
  locale: ref(defaultLocale as string),
  ogLocale: ref(''),
  hreflangLinks: ref<{ rel: string, hreflang: string, href: string }[]>([]),
  isInit: false
}

export function initReactivePageMetaData() {
  useHead(computed(() => ({
    title: reactiveHeadData.title.value,
    meta: [
      {
        property: 'og:title',
        content: reactiveHeadData.title.value
      },
      {
        name: 'description',
        content: reactiveHeadData.description.value
      },
      {
        property: 'og:description',
        content: reactiveHeadData.description.value
      },
      {
        property: 'og:url',
        content: reactiveHeadData.path.value
      },
      {
        property: 'og:image',
        content: reactiveHeadData.image.value
      },
      {
        property: 'og:locale',
        content: reactiveHeadData.ogLocale.value
      }
    ],
    link: [
      {
        rel: 'canonical',
        href: reactiveHeadData.path.value
      },
      ...reactiveHeadData.hreflangLinks.value
    ],
    htmlAttrs: {
      lang: reactiveHeadData.locale.value
    },
    bodyAttrs: {
      class: reactiveHeadData.bodyClass.value
    }
  })))
}

export function setPageMetaData(data: PageBasicData, variables: Record<string, unknown> | undefined = undefined) {
  if (isString(data.title)) {
    reactiveHeadData.title.value = replaceTemplateVariables(data.title, variables)
  }
  if (isString(data.description)) {
    reactiveHeadData.description.value = replaceTemplateVariables(data.description, variables)
  }
  if (isString(data.path)) {
    reactiveHeadData.path.value = `${import.meta.env.VITE_HOST || ''}${data.path}`
  }
  if (isString(data.image)) {
    reactiveHeadData.image.value = data.image
  }
  if (isString(data.bodyClass)) {
    reactiveHeadData.bodyClass.value = data.bodyClass
  }
}

let vue: App
let vueRouter: Router

function guardBeforeEnter(to: RouteLocation) {
  if ((to.path === '/unsubscribe/' || to.path === '/unsubscribe') && !!to.query?.token) {
    siteUtils.unsubscribeToken.value = String(to.query.token)
    return { path: '/' }
  }
  // "manage GOAT account" link from the store - take the token out of the URL before the navigation completes (no page_view
  // or other GA hit ever sees it); the values wait in memory for siteUtils.updateMode(). Runs on every navigation, not
  // only the first one - the token survives the "/" -> "/pl" locale redirect. Incomplete links are stripped too.
  const { link, query, stripped } = takeGoatLinkParams(to.query)
  if (stripped) {
    if (link) {
      pendingGoatLink.value = link
    }
    return { path: to.path, query, hash: to.hash }
  }
  return true
}

// Dedup guard shared by afterEach and the consent-ready catch-up; keyed on path+search+hash - #login / #register screens
// of the home page are separate page_views (as before)
let lastGaPageviewTrack = ''
// The only page_view sender (one per screen). vue-gtag's own `pageTracker` was removed - it sent a second page_view per
// navigation (next to gaEvent('page_view')), with the previous page's title and unhandled router.isReady() rejections.
// vue-gtag pageview() keeps the UTM -> campaign handling. GA4 enhanced measurement "page changes based on browser history
// events" must stay OFF in the GOAT GA4 properties.
function trackGaPageview() {
  const params = new URLSearchParams(window?.location?.search ?? '')
  if (SENSITIVE_QUERY_KEYS.some(key => params.has(key))) {
    return // the guard redirect to a clean URL follows - track that one
  }
  const pagePath = window?.location?.pathname ?? ''
  const trackKey = `${pagePath}${window?.location?.search ?? ''}${window?.location?.hash ?? ''}`
  if (trackKey === lastGaPageviewTrack) {
    return
  }
  lastGaPageviewTrack = trackKey
  pageview({
    ...gaUtils.gaPageViewParams({
      // reactiveHeadData.title (not document.title) - useHead updates the DOM <title> on a later tick, so document.title
      // still holds the previous page's title in afterEach
      page_title: reactiveHeadData.title.value || document?.title || '',
      page_location: window?.location?.href ?? '',
      page_path: pagePath
    }),
    send_page_view: false
  })
}

function getSkeletonRoutes(): RouteRecordRaw[] {
  return [
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: () => import('@/pages/404/ErrorPage.vue'),
      beforeEnter: [guardBeforeEnter as NavigationGuard]
    },
    {
      path: '/',
      name: 'home',
      component: MainLayout,
      beforeEnter: [guardBeforeEnter as NavigationGuard],
      children: []
    }
  ]
}

function routerSetPageMetaDataCallBack(to: RouteLocationNormalized) {
  const data: PageMetaData = to
  if (!reactiveHeadData.isInit) {
    initReactivePageMetaData()
    reactiveHeadData.isInit = true
  }
  const resetData: PageBasicData = {
    title: '',
    description: '',
    image: '',
    path: data.meta?.pageData?.path || to.path || '',
    bodyClass: ''
  }
  setPageMetaData(
    { ...resetData, ...data.meta.pageData },
    { ...data.meta?.pageData?.templateVariables, ...to.query, ...to.params }
  )
  const routeLocale = getRouteLocale(to)
  reactiveHeadData.locale.value = routeLocale
  reactiveHeadData.ogLocale.value = localeToOgLocale[routeLocale as Locale] ?? routeLocale
  const routerGroup = data.meta?.pageData?.routerGroup || ''
  reactiveHeadData.hreflangLinks.value = routerGroup
    ? buildHreflangLinks(routerGroup, toStringParams(to.params))
    : []
  setI18nLocale(routeLocale)
  if (!import.meta.env.SSR) {
    document.documentElement.lang = routeLocale
  }
}

export function routerSsgSetPageMetaDataCallBack(to: RouteLocationNormalized) {
  const data: PageMetaData = to
  const locale = data.meta.pageData?.locale || defaultLocale
  const routerGroup = data.meta.pageData?.routerGroup || ''
  const ogLocale = localeToOgLocale[locale as Locale] ?? locale
  const hreflangLinks = routerGroup
    ? buildHreflangLinks(routerGroup)
    : []
  useHead({
    title: data.meta.pageData?.title ?? '',
    meta: [
      {
        property: 'og:title',
        content: data.meta.pageData?.title ?? ''
      },
      {
        name: 'description',
        content: data.meta.pageData?.description ?? ''
      },
      {
        property: 'og:description',
        content: data.meta.pageData?.description ?? ''
      },
      {
        property: 'og:url',
        content: `${import.meta.env.VITE_HOST || ''}${to.path}`
      },
      {
        property: 'og:image',
        content: data.meta.pageData?.image ?? ''
      },
      {
        property: 'og:locale',
        content: ogLocale
      }
    ],
    link: [
      {
        rel: 'canonical',
        href: `${import.meta.env.VITE_HOST || ''}${to.path}`
      },
      ...hreflangLinks
    ],
    htmlAttrs: {
      lang: locale
    },
    bodyAttrs: {
      class: data.meta.pageData?.bodyClass ?? ''
    }
  })
}

function initRouter() {
  const skeletonRoutes = getSkeletonRoutes()
  if (skeletonRoutes[1]) {
    skeletonRoutes[1].children = pagesRoutes()
  }
  skeletonRoutes.push(
    ...[
      {
        path: '/gamesture-terms-of-service.pdf',
        beforeEnter: () => {
          window.location.href = 'https://gamesture.com/terms-of-service'
        },
        component: MainLayout
      },
      {
        path: '/gamesture-terms-of-service.html',
        beforeEnter: () => {
          window.location.href = 'https://gamesture.com/terms-of-service'
        },
        component: MainLayout
      },
      {
        path: '/gamesture-privacy-policy.pdf',
        beforeEnter: () => {
          window.location.href = 'https://store.gamesture.com/privacy-policy'
        },
        component: MainLayout
      },
      {
        path: '/gamesture-privacy-policy.html',
        beforeEnter: () => {
          window.location.href = 'https://store.gamesture.com/privacy-policy'
        },
        component: MainLayout
      }
    ]
  )

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: skeletonRoutes,
    scrollBehavior(to) {
      if (to.hash) {
        return false
      }
      return { top: 0, behavior: 'smooth' }
    }
  })

  /**
   * Locale redirect: runs before any component mounts to avoid flash of wrong locale.
   */
  router.beforeEach((to) => {
    if (typeof window === 'undefined')
      return
    if (supportedLocales.length <= 1)
      return

    // flang: forced language — override preferred-locale and redirect
    const flangValue = to.query.flang
    if (flangValue !== undefined) {
      const { flang: _, ...queryWithoutFlang } = to.query
      if (typeof flangValue === 'string' && isSupportedLocale(flangValue)) {
        localStorage.setItem('preferred-locale', flangValue)
        const currentLoc = getRouteLocale(to)
        if (flangValue !== currentLoc) {
          const newPath = getLocalePath(to.meta.pageData?.routerGroup, to.params, flangValue)
          if (newPath && newPath !== to.path)
            return { path: newPath, query: queryWithoutFlang }
        }
      }
      return { path: to.path, query: queryWithoutFlang }
    }

    const currentLoc = getRouteLocale(to)
    // URL locale takes priority for non-default locales (/pl/..., /de/...):
    // respect the explicit URL choice over localStorage / autoDetect preferences.
    if (currentLoc !== defaultLocale) {
      return
    }
    const preferred = localStorage.getItem('preferred-locale')
    if (preferred && isSupportedLocale(preferred) && preferred !== currentLoc) {
      const newPath = getLocalePath(to.meta.pageData?.routerGroup, to.params, preferred)
      if (newPath && newPath !== to.path)
        return { path: newPath, query: to.query }
    }
    if (autoDetectLanguage && !preferred) {
      const detected = navigator.language.split('-')[0]
      if (isSupportedLocale(detected) && detected !== currentLoc) {
        const newPath = getLocalePath(to.meta.pageData?.routerGroup, to.params, detected)
        if (newPath && newPath !== to.path)
          return { path: newPath, query: to.query }
      }
    }
  })

  /**
   * Sync i18n locale with the target route locale before beforeEnter guards run.
   * This ensures spaI18n.global.t() returns the correct translation in guardBeforeEnter.
   */
  router.beforeEach((to) => {
    if (typeof window === 'undefined')
      return
    setI18nLocale(getRouteLocale(to) as Locale)
  })

  /**
   * Start loaderPage
   */
  router.beforeEach((to, from) => {
    if (from.path && to.path !== from.path) {
      loadingPage.value = true
    }
  })

  /**
   * Register SEO, finish loaderPage, sync i18n locale
   */
  router.afterEach((to, _from, failure) => {
    loadingPage.value = false
    routerSetPageMetaDataCallBack(to)
    // aborted / duplicated navigation (e.g. the same #login pushed twice) - nothing new to track
    if (!failure && appUtilsPlugin.siteGtag()) {
      trackGaPageview()
    }
  })
  // catch-up: the first page_view of a visit is skipped above while gtag.js is still loading (or before consent) -
  // send it once GA becomes ready; the trackGaPageview() dedup guard prevents a double hit for the same screen
  watch(() => Boolean(appUtilsPlugin.siteGtag()), (isReady, wasReady) => {
    if (isReady && !wasReady) {
      trackGaPageview()
    }
  })

  /**
   * Register deploy-version checker on router error when SSG;
   * (preventing failures due to the implementation of a new version)
   */
  router.onError((_, to) => {
    const isSsr = import.meta.env.SSR
    const isDevMode = import.meta.env.MODE === 'development'
    if (!isSsr && !isDevMode) {
      let deployVersion = localStorage?.getItem?.('deploy-version')
      if (deployVersion && deployVersion?.length > 50) {
        deployVersion = ''
        localStorage.setItem?.('deploy-version', deployVersion)
      }
      fetch('/build.txt')
        .then((response) => {
          if (response.ok) {
            return response.text()
          } else {
            Sentry.captureException(new Error('Failed to fetch data (extra response)'), {
              extra: {
                response,
                msg: 'in router.onError'
              }
            })
          }
        })
        .then((buildTxt) => {
          if (buildTxt && buildTxt?.length <= 50) {
            if (buildTxt !== deployVersion) {
              localStorage?.setItem?.('deploy-version', buildTxt)
              if (to?.fullPath && window?.location) {
                const connector = Object.keys(to.query).length > 0 ? '&' : '?'
                window.location.href = `${to.fullPath}${connector}__nc=${Date.now()}`
              } else {
                location?.reload?.()
              }
            }
          } else {
            if (Sentry?.getClient()) {
              Sentry.captureException(new Error('not valid buildTxt'), {
                extra: {
                  to: to?.fullPath || window.location.href || location?.href,
                  buildTxt: buildTxt?.length ? buildTxt?.substring(0, 50) : buildTxt,
                  msg: 'not valid buildTxt in router.onError'
                }
              })
            }
          }
        })
        .catch((error) => {
          if (Sentry?.getClient()) {
            Sentry.captureException(error, {
              extra: {
                msg: 'catch error in router.onError'
              }
            })
          }
        })
    }
  })

  vue.use(router)
  vueRouter = router
  setVueRouter(router)

  const gtagId = import.meta.env.VITE_PROD ? 'G-41PYCXNRBS' : 'G-XPP7Q4FN32'
  const gtag = createGtag({
    tagId: gtagId,
    appName: 'GOAT - Gamesture',
    config: {
      id: gtagId,
      send_page_view: false
    },
    hooks: {
      // the only place optGtag is set: vue-gtag's addGtag() resolves even when gtag.js fails to load (blocked), this hook
      // fires only on a real script onload. Set before onReadyVueGtag - same order as the store's gtagReady.
      'script:loaded': () => {
        optGtag.value = true
        gaUtils.onReadyVueGtag(gtagId)
      }
    },
    initMode: 'manual'
  })
  vue.use(gtag)
}

export function initSsgRoutes() {
  const skeletonRoutes = []
  const routes = getSkeletonRoutes()?.[1]
  if (routes) {
    skeletonRoutes.push(routes)
  }
  if (skeletonRoutes[0]) {
    skeletonRoutes[0].children = pagesRoutes().flatMap((routeData) => {
      // Bind locale params in path (e.g. /:locale(pl)/polityka-prywatnosci → /pl/polityka-prywatnosci)
      // and assign name = path so vite-ssg generates the correct HTML file.
      routeData.path = bindParams(routeData.path as string, routeData.meta?.pageData?.templateVariables)
      routeData.name = routeData.path
      return routeData
    })
  }
  return skeletonRoutes
}

export default {
  install: (app: App) => {
    vue = app
    initRouter()
  },
  getVueRouter() {
    return vueRouter
  }
}
